import pluralize from 'pluralize'
import { PropsWithChildren } from 'react'

import { GoogleMapsProvider } from 'src/providers/GoogleMapsProvider'

import { Divider } from 'src/components/atoms/Divider'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { MultiPointMap } from 'src/components/molecules/MultiPointMap'
import { MultiPointMapGeo } from 'src/components/molecules/MultiPointMap/types'

import { viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'

interface ProjectMapBaseProps {
  points: MultiPointMapGeo[]
  mapHeight?: string
  treeCountLabel?: string
}

const ProjectMapBase = ({
  points,
  mapHeight = 'h-[500px]',
  treeCountLabel = 'received so far'
}: ProjectMapBaseProps) => {
  const renderInfoContent = (point: MultiPointMapGeo) => {
    const LinkContainer = ({ children }: PropsWithChildren) =>
      point.info?.isPublicPage ? (
        <InternalLink
          openInNewTab
          href={viewProjectPath({ routeParams: { uri: point.info?.uri! } })}
        >
          {children}
        </InternalLink>
      ) : (
        <div>{children}</div>
      )

    const ProjectLink = () => (
      <InternalLink
        openInNewTab
        className='text-[16px]'
        href={viewProjectPath({ routeParams: { uri: point.info?.uri! } })}
      >
        View planting project page
      </InternalLink>
    )

    return (
      <div className='ml-1 flex flex-col w-[220px] gap-[10px]'>
        <LinkContainer>
          <div className='relative w-full h-[100px] rounded-md overflow-hidden'>
            <Image
              alt={point.info?.title || 'project hero'}
              className='object-cover'
              fill
              src={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${point.info?.imageUrl}`}
            />
          </div>
        </LinkContainer>
        <span className='font-bold text-[18px] leading-[20px]'>
          {point.info?.title}
        </span>
        <Divider className='m-0!' />
        <span className='font-medium text-[16px]'>
          {pluralize('tree', Number(point.info?.value), true)} {treeCountLabel}
        </span>
        {point.info?.uri && point.info?.isPublicPage ? <ProjectLink /> : null}
      </div>
    )
  }

  return (
    <div className={`w-full ${mapHeight}`}>
      <GoogleMapsProvider>
        <MultiPointMap
          pinIcon='/img/icons/treeLocationPin.svg'
          geoData={points}
          zoom={12}
          clustered
          constrainMap
          mapProps={{ mapContainerStyle: { width: '100%', height: '100%' } }}
          renderInfoContent={renderInfoContent}
        />
      </GoogleMapsProvider>
    </div>
  )
}

export default ProjectMapBase
